import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Shield, 
  Users, 
  CheckCircle, 
  Star, 
  AlertTriangle,
  Camera,
  Clock,
  Crown,
  Eye,
  Briefcase,
  AlertCircle
} from 'lucide-react';

const FairRateHomepage = () => {
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [liveStats, setLiveStats] = useState({
    modelsToday: 847,
    wagesCalculated: 2347231,
    predatoryBlocked: 1247,
    activeNow: 23
  });

  const [formData, setFormData] = useState({
    shootType: 'portrait',
    paymentType: 'cash',
    hours: 2,
    looks: 1,
    themes: 1,
    callTime: 'standard',
    clientMarket: 'local-business',
    shootLocation: 'local',
    travel: 'none',
    includeLunch: false,
    distribution: 'social',
    usageLength: '1year',
    socialFollowers: 'under10k',
    rushJob: false,
    clientExperience: 'established',
    hairMakeup: 'provided',
    wardrobe: 'provided',
    fittings: 'none',
    payoutDays: '30days',
    equityPercent: 0,
    nonProfitDiscount: 0
  });

  const [calculatedPay, setCalculatedPay] = useState(0);
  const [recommendation, setRecommendation] = useState({ color: '', text: '', icon: '' });

  const shootTypes = {
    portrait: { base: 150, label: 'Portrait/Headshots' },
    fashion: { base: 250, label: 'Fashion Editorial' },
    commercial: { base: 350, label: 'Commercial/Advertising' },
    fitness: { base: 200, label: 'Fitness/Athletic' },
    beauty: { base: 180, label: 'Beauty/Cosmetics' },
    lingerie: { base: 300, label: 'Lingerie/Swimwear' },
    lifestyle: { base: 220, label: 'Lifestyle/Brand' }
  };

  const paymentTypes = {
    cash: { label: 'Cash Payment', multiplier: 1.0 },
    nonprofit: { label: 'Non-Profit Organization', multiplier: 1.0 },
    equity: { label: 'Cash + Equity Combination', multiplier: 1.0 },
    deferred: { label: 'Deferred Payment', multiplier: 1.15 }
  };

  const payoutTimelines = {
    'immediate': { multiplier: 0.98, label: 'Day of Shoot (2% client discount)' },
    '7days': { multiplier: 1.0, label: 'Within 7 Days (Standard)' },
    '15days': { multiplier: 1.0, label: 'Within 15 Days (Standard)' },
    '30days': { multiplier: 1.03, label: 'Within 30 Days (+3% cash flow fee)' },
    '45days': { multiplier: 1.06, label: 'Within 45 Days (+6% extended terms)' },
    '60days': { multiplier: 1.10, label: 'Within 60+ Days (+10% late payment fee)' }
  };

  const clientMarkets = {
    'fortune500': { multiplier: 1.8, label: 'Fortune 500/National Brand' },
    'tier1-agency': { multiplier: 1.6, label: 'Major Market Agency (NYC/LA/Chicago)' },
    'tier2-agency': { multiplier: 1.3, label: 'Regional Agency/Mid-Market Brand' },
    'local-business': { multiplier: 1.0, label: 'Local/Regional Business' },
    'startup': { multiplier: 0.9, label: 'Startup/Small Business' },
    'individual': { multiplier: 0.8, label: 'Individual/Personal Project' }
  };

  const shootLocations = {
    'local': { multiplier: 1.0, label: 'Local to Me (No Travel)' },
    'regional': { multiplier: 1.1, label: 'Regional Travel (Under 100 miles)' },
    'destination': { multiplier: 1.3, label: 'Destination Travel (100+ miles)' },
    'international': { multiplier: 1.5, label: 'International Location' },
    'remote': { multiplier: 0.95, label: 'Remote/Virtual Content Creation' }
  };

  const distributionMultipliers = {
    social: { multiplier: 1.0, label: 'Social Media/Instagram Only' },
    website: { multiplier: 1.3, label: 'Website/Online Portfolio' },
    print: { multiplier: 1.8, label: 'Print/Magazine' },
    billboard: { multiplier: 2.5, label: 'Billboard/Outdoor Advertising' },
    tv: { multiplier: 3.0, label: 'TV/Commercial' },
    national: { multiplier: 4.0, label: 'National Campaign' }
  };

  const usageLengthMultipliers = {
    '3months': { multiplier: 0.8, label: '3 Months' },
    '6months': { multiplier: 1.0, label: '6 Months' },
    '1year': { multiplier: 1.2, label: '1 Year' },
    '2years': { multiplier: 1.5, label: '2 Years' },
    '3years': { multiplier: 1.8, label: '3 Years' },
    'unlimited': { multiplier: 2.5, label: 'Unlimited/Buyout' }
  };

  const socialFollowersMultipliers = {
    'under10k': 1.0,
    '10k-50k': 1.1,
    '50k-100k': 1.2,
    '100k-500k': 1.4,
    '500k-1m': 1.6,
    'over1m': 2.0
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveStats(prev => ({
        modelsToday: prev.modelsToday + Math.floor(Math.random() * 3),
        wagesCalculated: prev.wagesCalculated + Math.floor(Math.random() * 500),
        predatoryBlocked: prev.predatoryBlocked + Math.floor(Math.random() * 2),
        activeNow: 15 + Math.floor(Math.random() * 25)
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    calculatePay();
  }, [formData]);

  const calculatePay = () => {
    let basePay = shootTypes[formData.shootType].base;
    
    const hourlyRate = basePay / 2;
    let totalPay = Math.max(basePay, hourlyRate * formData.hours);
    
    const looksMultiplier = 1 + (formData.looks - 1) * 0.2;
    const themesMultiplier = 1 + (formData.themes - 1) * 0.15;
    totalPay *= looksMultiplier * themesMultiplier;
    
    if (formData.callTime === 'early') totalPay *= 1.15;
    if (formData.callTime === 'late') totalPay *= 1.20;
    if (formData.callTime === 'weekend') totalPay *= 1.25;
    
    totalPay *= clientMarkets[formData.clientMarket].multiplier;
    totalPay *= shootLocations[formData.shootLocation].multiplier;
    
    if (formData.travel === 'local') totalPay += 30;
    if (formData.travel === 'regional') totalPay += 75;
    if (formData.travel === 'long') totalPay += 150;
    
    if (!formData.includeLunch) totalPay += 20;
    
    totalPay *= distributionMultipliers[formData.distribution].multiplier;
    totalPay *= usageLengthMultipliers[formData.usageLength].multiplier;
    totalPay *= socialFollowersMultipliers[formData.socialFollowers];
    
    if (formData.rushJob) totalPay *= 1.35;
    
    if (formData.clientExperience === 'new') totalPay *= 1.15;
    if (formData.clientExperience === 'questionable') totalPay *= 1.30;
    
    if (formData.hairMakeup === 'model') totalPay += 50;
    if (formData.hairMakeup === 'partial') totalPay += 25;
    
    if (formData.wardrobe === 'model') totalPay += 75;
    if (formData.wardrobe === 'partial') totalPay += 35;
    
    if (formData.fittings === 'required') totalPay += 50;
    if (formData.fittings === 'multiple') totalPay += 100;
    
    totalPay *= payoutTimelines[formData.payoutDays].multiplier;
    
    if (formData.paymentType === 'nonprofit') {
      const discountAmount = (formData.nonProfitDiscount / 100) * totalPay;
      totalPay = Math.max(totalPay - discountAmount, basePay * 0.7);
    }
    
    if (formData.paymentType === 'equity') {
      const equityValue = (formData.equityPercent / 100) * totalPay;
      totalPay = totalPay - equityValue;
    }
    
    if (formData.paymentType === 'deferred') {
      totalPay *= paymentTypes.deferred.multiplier;
    }
    
    const finalPay = Math.round(totalPay);
    setCalculatedPay(finalPay);
    
    if (finalPay < 120) {
      setRecommendation({
        color: 'bg-red-100 border-red-500 text-red-900',
        text: '🚨 PREDATORY RATE - DECLINE THIS OFFER',
        icon: 'danger'
      });
    } else if (finalPay < 180) {
      setRecommendation({
        color: 'bg-red-100 border-red-400 text-red-800',
        text: '⚠️ Below Fair Market - High risk, negotiate higher',
        icon: 'warning'
      });
    } else if (finalPay < 280) {
      setRecommendation({
        color: 'bg-yellow-100 border-yellow-400 text-yellow-800',
        text: 'Low but potentially acceptable - Negotiate upward',
        icon: 'caution'
      });
    } else if (finalPay < 500) {
      setRecommendation({
        color: 'bg-green-100 border-green-400 text-green-800',
        text: 'Fair compensation - This seems reasonable',
        icon: 'check'
      });
    } else if (finalPay < 800) {
      setRecommendation({
        color: 'bg-blue-100 border-blue-400 text-blue-800',
        text: 'Great rate - Above market average',
        icon: 'star'
      });
    } else {
      setRecommendation({
        color: 'bg-purple-100 border-purple-400 text-purple-800',
        text: 'Excellent rate - Premium compensation',
        icon: 'star'
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case 'danger': return <AlertTriangle className="w-5 h-5" />;
      case 'warning': return <AlertCircle className="w-5 h-5" />;
      case 'caution': return <AlertCircle className="w-5 h-5" />;
      case 'check': return <CheckCircle className="w-5 h-5" />;
      case 'star': return <Star className="w-5 h-5" />;
      default: return <AlertCircle className="w-5 h-5" />;
    }
  };

  const generateQuoteSummary = () => {
    const summary = [];
    summary.push(`• ${shootTypes[formData.shootType].label} shoot`);
    summary.push(`• ${formData.hours} hour${formData.hours > 1 ? 's' : ''}`);
    summary.push(`• ${formData.looks} look${formData.looks > 1 ? 's' : ''}, ${formData.themes} theme${formData.themes > 1 ? 's' : ''}`);
    summary.push(`• Client: ${clientMarkets[formData.clientMarket].label}`);
    summary.push(`• Location: ${shootLocations[formData.shootLocation].label}`);
    summary.push(`• ${distributionMultipliers[formData.distribution].label}`);
    summary.push(`• ${usageLengthMultipliers[formData.usageLength].label} usage`);
    summary.push(`• Payment: ${payoutTimelines[formData.payoutDays].label}`);
    
    if (formData.rushJob) summary.push('• Rush job (less than 48hrs notice)');
    if (!formData.includeLunch) summary.push('• No lunch/catering provided');
    
    return summary;
  };

  const testimonials = [
    {
      text: "This calculator saved me from accepting a $150 shoot that should have been $300!",
      author: "Sarah M.",
      role: "Fashion Model"
    },
    {
      text: "I've increased my average rate by $200 since using this tool",
      author: "Anonymous",
      role: "Commercial Model"
    },
    {
      text: "Finally, a tool that puts models first!",
      author: "Jessica K.",
      role: "Lifestyle Model"
    }
  ];

  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <header className="bg-white bg-opacity-80 border-b border-purple-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">FairRate</h1>
                <p className="text-xs text-gray-600">Protecting Model Rights</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-gray-600 hover:text-purple-600 font-medium">About</button>
              <button className="text-gray-600 hover:text-purple-600 font-medium">Resources</button>
              <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">Sign In</button>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center space-x-8 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>{liveStats.activeNow} models active now</span>
            </div>
            <div>💫 {liveStats.modelsToday.toLocaleString()} models used this today</div>
            <div>💰 ${liveStats.wagesCalculated.toLocaleString()} in fair wages calculated</div>
            <div>🛡️ {liveStats.predatoryBlocked} predatory offers blocked</div>
          </div>
        </div>
      </div>

      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              <span>Trusted by 12,000+ Models Nationwide</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Stop Getting
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600"> Underpaid</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The first AI-powered rate calculator designed specifically to protect freelance models from predatory practices and ensure fair compensation.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 mb-12 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>
              <blockquote className="text-lg text-gray-800 mb-4">
                "{testimonials[currentTestimonial].text}"
              </blockquote>
              <div className="text-sm text-gray-600">
                <span className="font-semibold">{testimonials[currentTestimonial].author}</span> • {testimonials[currentTestimonial].role}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Calculate Your Fair Rate - Free Forever</h2>
            <p className="text-lg text-gray-600">Get instant, industry-accurate rate calculations with built-in predatory protection</p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Camera className="w-5 h-5 mr-2" />
                  Shoot Details
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type of Shoot</label>
                    <select 
                      value={formData.shootType}
                      onChange={(e) => handleInputChange('shootType', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(shootTypes).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Type</label>
                    <select 
                      value={formData.paymentType}
                      onChange={(e) => handleInputChange('paymentType', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(paymentTypes).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hours: {formData.hours}</label>
                    <input
                      type="range"
                      min="1"
                      max="12"
                      value={formData.hours}
                      onChange={(e) => handleInputChange('hours', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1hr</span>
                      <span>12hrs</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Number of Looks: {formData.looks}</label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={formData.looks}
                      onChange={(e) => handleInputChange('looks', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Themes/Concepts: {formData.themes}</label>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={formData.themes}
                      onChange={(e) => handleInputChange('themes', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Logistics and Services
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Call Time</label>
                    <select 
                      value={formData.callTime}
                      onChange={(e) => handleInputChange('callTime', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="standard">Standard Hours (9AM-6PM)</option>
                      <option value="early">Early Morning (Before 9AM)</option>
                      <option value="late">Evening/Night (After 6PM)</option>
                      <option value="weekend">Weekend</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Timeline</label>
                    <select 
                      value={formData.payoutDays}
                      onChange={(e) => handleInputChange('payoutDays', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(payoutTimelines).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hair and Makeup</label>
                    <select 
                      value={formData.hairMakeup}
                      onChange={(e) => handleInputChange('hairMakeup', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="provided">Provided by Client</option>
                      <option value="partial">Model Provides Partial</option>
                      <option value="model">Model Provides All</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Wardrobe</label>
                    <select 
                      value={formData.wardrobe}
                      onChange={(e) => handleInputChange('wardrobe', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="provided">Provided by Client</option>
                      <option value="partial">Model Provides Partial</option>
                      <option value="model">Model Provides All</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fittings</label>
                    <select 
                      value={formData.fittings}
                      onChange={(e) => handleInputChange('fittings', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="none">No Fittings Required</option>
                      <option value="required">One Fitting Required</option>
                      <option value="multiple">Multiple Fittings Required</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.includeLunch}
                      onChange={(e) => handleInputChange('includeLunch', e.target.checked)}
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label className="text-sm font-medium text-gray-700">Lunch/Catering Provided</label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.rushJob}
                      onChange={(e) => handleInputChange('rushJob', e.target.checked)}
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label className="text-sm font-medium text-gray-700">Rush Job (Less than 48hrs notice)</label>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2" />
                  Client and Usage Details
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Type/Market</label>
                    <select 
                      value={formData.clientMarket}
                      onChange={(e) => handleInputChange('clientMarket', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(clientMarkets).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-600 mt-1">Based on client's budget capacity, not your location</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shoot Location</label>
                    <select 
                      value={formData.shootLocation}
                      onChange={(e) => handleInputChange('shootLocation', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(shootLocations).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-600 mt-1">Where the actual shoot takes place</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Distribution Plan</label>
                    <select 
                      value={formData.distribution}
                      onChange={(e) => handleInputChange('distribution', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(distributionMultipliers).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Length of Usage</label>
                    <select 
                      value={formData.usageLength}
                      onChange={(e) => handleInputChange('usageLength', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {Object.entries(usageLengthMultipliers).map(([key, value]) => (
                        <option key={key} value={key}>{value.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client's Social Media Reach</label>
                    <select 
                      value={formData.socialFollowers}
                      onChange={(e) => handleInputChange('socialFollowers', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="under10k">Under 10K followers</option>
                      <option value="10k-50k">10K - 50K followers</option>
                      <option value="50k-100k">50K - 100K followers</option>
                      <option value="100k-500k">100K - 500K followers</option>
                      <option value="500k-1m">500K - 1M followers</option>
                      <option value="over1m">Over 1M followers</option>
                    </select>
                    <p className="text-xs text-gray-600 mt-1">Higher reach = higher image value</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Experience</label>
                    <select 
                      value={formData.clientExperience}
                      onChange={(e) => handleInputChange('clientExperience', e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="established">Established/Well-Known</option>
                      <option value="new">New/Unproven Client</option>
                      <option value="questionable">Questionable History</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg sticky top-24">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Your Fair Rate
                </h3>
                
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-gray-900 mb-2">${calculatedPay}</div>
                  <div className="text-sm text-gray-600">Recommended minimum rate</div>
                </div>

                <div className={`p-4 rounded-lg border-2 ${recommendation.color} mb-6`}>
                  <div className="flex items-center">
                    {getIcon(recommendation.icon)}
                    <span className="ml-2 font-medium text-sm">{recommendation.text}</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-4 text-white mb-6">
                  <div className="flex items-center mb-2">
                    <Crown className="w-5 h-5 mr-2" />
                    <span className="font-semibold">Want More Insights?</span>
                  </div>
                  <p className="text-sm text-purple-100 mb-3">
                    Upgrade to see client reputation, historical rates, and professional PDF exports
                  </p>
                  <button 
                    onClick={() => setShowPremiumModal(true)}
                    className="w-full bg-white text-purple-600 py-2 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors"
                  >
                    Try Premium Free
                  </button>
                </div>

                <div className="bg-white p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-3">Quote Summary:</h4>
                  <div className="text-sm space-y-1">
                    {generateQuoteSummary().slice(0, 6).map((item, index) => (
                      <div key={index} className="text-gray-700">{item}</div>
                    ))}
                    {generateQuoteSummary().length > 6 && (
                      <div className="text-purple-600 text-sm font-medium cursor-pointer">
                        + {generateQuoteSummary().length - 6} more details
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Rate Breakdown:</h4>
                  <div className="text-sm space-y-2">
                    <div className="flex justify-between">
                      <span>Base Rate:</span>
                      <span>${shootTypes[formData.shootType].base}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Client Market:</span>
                      <span>×{clientMarkets[formData.clientMarket].multiplier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shoot Location:</span>
                      <span>×{shootLocations[formData.shootLocation].multiplier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Distribution Rights:</span>
                      <span>×{distributionMultipliers[formData.distribution].multiplier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Usage Length:</span>
                      <span>×{usageLengthMultipliers[formData.usageLength].multiplier}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Social Reach:</span>
                      <span>×{socialFollowersMultipliers[formData.socialFollowers]}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Payment Timeline:</span>
                      <span>×{payoutTimelines[formData.payoutDays].multiplier}</span>
                    </div>
                    {formData.rushJob && (
                      <div className="flex justify-between">
                        <span>Rush Job:</span>
                        <span>+35%</span>
                      </div>
                    )}
                    <hr className="my-2" />
                    <div className="flex justify-between font-semibold">
                      <span>Total:</span>
                      <span>${calculatedPay}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-200">
                <h4 className="text-lg font-semibold mb-4 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-green-500" />
                  Live Activity
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-400 rounded-full mt-2 animate-pulse"></div>
                    <div className="text-sm">
                      <div className="font-medium">Just now</div>
                      <div className="text-gray-600">Model in Atlanta calculated $425 for commercial shoot</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                    <div className="text-sm">
                      <div className="font-medium">2 min ago</div>
                      <div className="text-gray-600">Model avoided $100 underpayment in Nashville</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                    <div className="text-sm">
                      <div className="font-medium">5 min ago</div>
                      <div className="text-gray-600">Fashion rate calculated: $280 (Birmingham)</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <div className="text-sm font-medium text-purple-700">🔥 Most popular today:</div>
                  <div className="text-sm text-purple-600">Client reputation lookup</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-red-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-red-800 mb-6 flex items-center">
              <AlertTriangle className="w-8 h-8 mr-3" />
              🚨 CRITICAL RED FLAGS - DECLINE IMMEDIATELY
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">Requests for "test shots" or "portfolio building" without compensation</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">Vague usage rights or claims of "unlimited usage for social media"</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">Pressure to shoot immediately or "opportunity won't last"</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">No written contract, verbal agreements only</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">Client refuses to provide references or portfolio of past work</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">Payment promises "after images become successful"</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">Requests to meet in non-professional locations</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-red-600 text-lg">•</span>
                  <span className="text-red-700">No makeup artist/stylist present for intimate shoots</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for Model Safety</h2>
            <p className="text-xl text-gray-600">Industry-first protections against exploitation and predatory practices</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Predatory Alert System</h3>
              <p className="text-gray-600">Automatically flags suspicious job offers and below-market rates</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Client Verification</h3>
              <p className="text-gray-600">Crowd-sourced database of photographer and client reputation</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Community Support</h3>
              <p className="text-gray-600">Connect with other models and share experiences safely</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-2 rounded-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">FairRate</h1>
                  <p className="text-xs text-gray-400">Protecting Model Rights</p>
                </div>
              </div>
              <p className="text-gray-400">Empowering freelance models with fair compensation tools and anti-predatory protections.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Tools</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Rate Calculator</a></li>
                <li><a href="#" className="hover:text-white">Client Database</a></li>
                <li><a href="#" className="hover:text-white">Contract Templates</a></li>
                <li><a href="#" className="hover:text-white">Income Tracker</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Industry Reports</a></li>
                <li><a href="#" className="hover:text-white">Safety Guidelines</a></li>
                <li><a href="#" className="hover:text-white">Negotiation Tips</a></li>
                <li><a href="#" className="hover:text-white">Legal Resources</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 FairRate. All rights reserved. • Protecting models from predatory practices since 2025.</p>
          </div>
        </div>
      </footer>

      {showPremiumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-100 to-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Join Premium Waitlist</h3>
              <p className="text-gray-600 mb-6">Be the first to access advanced rate intelligence and client protection tools</p>
              
              <div className="text-left space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Historical rate tracking and analytics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Client reputation database access</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Professional PDF quote exports</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm">Income goal tracking and insights</span>
                </div>
              </div>

              <div className="space-y-3">
                <input 
                  type="email" 
                  placeholder="Enter your email address"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold">
                  Join Waitlist
                </button>
                <button 
                  onClick={() => setShowPremiumModal(false)}
                  className="w-full text-gray-600 py-3 rounded-lg font-medium hover:bg-gray-50"
                >
                  Maybe Later
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                Expected launch: Q2 2025 • Starting at $9/month • Early access discounts available
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FairRateHomepage;